import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AccountBalance,
  GeneralLedgerTransaction,
  Response,
  AccountType,
  ChangeLog,
  Pagination,
  Document,
  DocumentStatus,
  DocumentType,
  DocumentComment,
} from '@ui/src';
import { statementsItemApi } from '../services/item';

export interface GeneralTransactionState {
  isLoading: boolean;
  transaction?: GeneralLedgerTransaction;
  transactionId?: string;
  debitAccountBalance?: AccountBalance;
  creditAccountBalance?: AccountBalance;
  transactionChangeLogs?: ChangeLog[];
  transactionChangeLogsPagination?: Pagination;
  documents?: Document[];
  documentsPagination?: Pagination;
  document?: Document;
}

const initialState: GeneralTransactionState = {
  isLoading: false,
  transaction: undefined,
  transactionId: undefined,
};

export const generalTransactionSlice = createSlice({
  name: 'generalTransaction',
  initialState,
  reducers: {
    clearTransactionState: (state) => {
      state.transaction = undefined;
      state.transactionId = undefined;
      state.transactionChangeLogs = undefined;
      state.transactionChangeLogsPagination = undefined;
      state.document = undefined;
      state.documents = undefined;
      state.documentsPagination = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        statementsItemApi.endpoints.getGeneralLedgerStatment.matchFulfilled,
        (state, action: PayloadAction<{ data: GeneralLedgerTransaction }>) => {
          state.transaction = action.payload.data;
        },
      )
      .addMatcher(
        statementsItemApi.endpoints.getStatementChangeLogs.matchFulfilled,
        (
          state,
          action: PayloadAction<Response<ChangeLog>> & {
            meta: { arg: { originalArgs: string } };
          },
        ) => {
          if (!action.meta.arg.originalArgs.includes('cursor')) {
            state.transactionChangeLogs = action.payload.data?.edges?.map(
              (item) => item.node,
            );
          } else {
            const nextChangeLogs = ([] as ChangeLog[]).concat(
              state.transactionChangeLogs as ChangeLog[],
            );

            action.payload.data.edges?.forEach((item) => {
              nextChangeLogs.push(item.node);
            });

            state.transactionChangeLogs = nextChangeLogs;
          }

          state.transactionChangeLogsPagination = action.payload.data?.pageInfo;
        },
      )
      .addMatcher(
        statementsItemApi.endpoints.getAccountBalanceById.matchFulfilled,
        (
          state,
          action: PayloadAction<Response<AccountBalance>> & {
            meta: {
              arg: {
                originalArgs: {
                  id: string;
                  query?: string | undefined;
                  typeAcc: AccountType;
                };
              };
            };
          },
        ) => {
          if (action.meta.arg.originalArgs.typeAcc) {
            const [accBalance] = action.payload.data.edges.map(
              (item) => item.node,
            );

            if (action.meta.arg.originalArgs.typeAcc === AccountType.DEBIT) {
              state.debitAccountBalance = accBalance;
            } else if (
              action.meta.arg.originalArgs.typeAcc === AccountType.CREDIT
            ) {
              state.creditAccountBalance = accBalance;
            }
          }
        },
      )
      .addMatcher(
        statementsItemApi.endpoints.getStatementsGLDocuments.matchFulfilled,
        (
          state,
          action: PayloadAction<Response<Document>> & {
            meta: {
              arg: {
                originalArgs: {
                  cursor?: string;
                };
              };
            };
          },
        ) => {
          let documents;

          if (!action.meta?.arg?.originalArgs?.cursor) {
            documents = action.payload.data.edges?.map((item) => item.node);
          } else {
            documents = ([] as Document[]).concat(
              state.documents as Document[],
            );

            action.payload.data.edges?.forEach((item) =>
              documents.push(item.node),
            );
          }

          state.documents = documents;
          state.documentsPagination = action.payload.data.pageInfo;
        },
      )
      .addMatcher(
        statementsItemApi.endpoints.addStatementDocumentToGLTransaction
          .matchFulfilled,
        (state, action: PayloadAction<{ data: Document }>) => {
          let nextDocuments: Document[] = [];

          if (state.documents) {
            nextDocuments = ([] as Document[]).concat(
              state.documents,
            );
          }

          nextDocuments.unshift(action.payload.data);

          state.documents = nextDocuments;
        },
      )
      .addMatcher(
        statementsItemApi.endpoints.getStatementsGLDocument.matchFulfilled,
        (state, action: PayloadAction<{ data: Document }>) => {
          const nextDocument = { ...action.payload.data };

          state.document = {
            ...nextDocument,
            commentsList: action.payload.data.comments?.edges.map(
              (item) => item.node,
            ),
          };
        },
      )
      .addMatcher(
        statementsItemApi.endpoints.deleteStatementsGLDocuments.matchFulfilled,
        (
          state,
          action: {
            meta: {
              arg: {
                originalArgs: {
                  body: {
                    ids: string[];
                  };
                };
              };
            };
          },
        ) => {
          const { ids } = action.meta.arg.originalArgs.body;

          const nextDocuments = state?.documents?.filter((item) =>
            ids.every((id) => item.id !== id),
          );

          state.document = undefined;

          state.documents = nextDocuments;
        },
      )
      .addMatcher(
        statementsItemApi.endpoints.changeStatementsDocumentStatus
          .matchFulfilled,
        (
          state,
          action: {
            meta: {
              arg: {
                originalArgs: {
                  body: {
                    ids: string[];
                    status: DocumentStatus;
                  };
                };
              };
            };
          },
        ) => {
          const { ids, status } = action.meta.arg.originalArgs.body;

          const nextDocuments = state?.documents?.map((item) => {
            const result = { ...item };
            const currentDocumentId = ids.find((id) => result.id === id);

            if (result.id === currentDocumentId) {
              result.status = status;
            }

            return result;
          });

          if (state.document) {
            const nextDocument = { ...state.document };

            if (ids.find((item) => item === nextDocument.id)) {
              nextDocument.status = status;

              state.document = nextDocument;
            }
          }

          state.documents = nextDocuments;
        },
      )
      .addMatcher(
        statementsItemApi.endpoints.changeStatementsDocumentCategory
          .matchFulfilled,
        (
          state,
          action: {
            meta: {
              arg: {
                originalArgs: {
                  body: {
                    ids: string[];
                    category: DocumentType;
                  };
                };
              };
            };
          },
        ) => {
          const { ids, category } = action.meta.arg.originalArgs.body;

          const nextDocuments = state?.documents?.map((item) => {
            const result = { ...item };
            const currentDocumentId = ids.find((id) => result.id === id);

            if (result.id === currentDocumentId) {
              result.category = category;
            }

            return result;
          });

          if (state.document) {
            const nextDocument = { ...state.document };

            if (ids.find((item) => item === nextDocument.id)) {
              nextDocument.category = category;

              state.document = nextDocument;
            }
          }

          state.documents = nextDocuments;
        },
      )
      .addMatcher(
        statementsItemApi.endpoints.addStatementsComment.matchFulfilled,
        (state, action: PayloadAction<{ data: DocumentComment }>) => {
          if (state.document) {
            const nextDocument = { ...state.document };
            let nextComments: DocumentComment[] = [];

            if (
              nextDocument.commentsList &&
              nextDocument.commentsList.length > 0
            ) {
              nextComments = ([] as DocumentComment[]).concat(
                nextDocument.commentsList,
              );
            }

            nextComments.push(action.payload.data);

            nextDocument.commentsList = nextComments;

            state.document = nextDocument;
          }
        },
      )
      .addMatcher(
        statementsItemApi.endpoints.editStatementsComment.matchFulfilled,
        (state, action: PayloadAction<{ data: DocumentComment }>) => {
          if (state.document) {
            const nextDocument = { ...state.document };
            nextDocument.commentsList = nextDocument.commentsList?.map(
              (item) => {
                let result = { ...item };

                if (result.id === action.payload.data.id) {
                  result = action.payload.data;
                }

                return result;
              },
            );

            state.document = nextDocument;
          }
        },
      )
      .addMatcher(
        statementsItemApi.endpoints.deleteStatementsComment.matchFulfilled,
        (state, action: PayloadAction<{ data: DocumentComment }>) => {
          if (state.document) {
            const nextDocument = { ...state.document };

            nextDocument.commentsList = nextDocument?.commentsList?.filter(
              (item) => item.id !== action.payload.data.id,
            );

            state.document = nextDocument;
          }
        },
      );
  },
});

export const { clearTransactionState } = generalTransactionSlice.actions;

export default generalTransactionSlice.reducer;
